// Relative path: src/utils/WebsiteUtils.js

// Import all website logos
import mmLogo from '../images/mm_logo.png';
import youtubeLogo from '../images/youtubelogo.svg';
import tiktokLogo from '../images/tiktoklogo.svg';
import HRNlogo from '../images/HRNlogo.png';
import logo_reddit from '../images/logo_reddit.png';
import hairlossexperiencesLogo from '../images/hairlossexperiences.png';
import reviewsioLogo from '../images/reviewsiologo.svg';
import mediumLogo from '../images/mediumlogo.png';

/**
 * Centralized website utilities for handling website logos, names, and URL detection
 * This replaces duplicated logic across multiple components
 */
class WebsiteUtils {
  constructor() {
    // Supported website identifiers, in order of precedence for detection
    this.websiteKeys = [
      "reddit",
      "youtube",
      "tiktok",
      "hairrestorationnetwork",
      "hairlossexperiences",
      "reviews.io",
      "medium"
    ];

    // Map of identifier → logo import
    this.logoMap = {
      reddit: logo_reddit,
      youtube: youtubeLogo,
      tiktok: tiktokLogo,
      hairrestorationnetwork: HRNlogo,
      hairlossexperiences: hairlossexperiencesLogo,
      "reviews.io": reviewsioLogo,
      medium: mediumLogo,
      "match medic": mmLogo
    };

    // Map of identifier → user-friendly display name
    this.displayNameMap = {
      reddit: "Reddit",
      youtube: "Youtube",
      tiktok: "TikTok",
      hairrestorationnetwork: "Hair Restoration Network",
      hairlossexperiences: "Hair Loss Experiences",
      "reviews.io": "Reviews.io",
      medium: "Medium",
      "match medic": "Match Medic"
    };
  }

  /**
   * Get website key from external URL
   * @param {string} externalUrl - The external URL to analyze
   * @returns {string} - Website key or "match medic" as fallback
   */
  getWebsiteKeyFromUrl(externalUrl) {
    if (!externalUrl || typeof externalUrl !== 'string') {
      return "match medic";
    }

    const lowerUrl = externalUrl.toLowerCase();
    
    // Check each website key to see if URL contains it
    for (const key of this.websiteKeys) {
      if (key === "reviews.io") {
        // Special case for reviews.io - check for reviews.io domain
        if (lowerUrl.includes("reviews.io")) {
          return key;
        }
      } else if (key === "hairrestorationnetwork") {
        // Special case for hairrestorationnetwork - might be abbreviated
        if (lowerUrl.includes("hairrestorationnetwork") || lowerUrl.includes("hrn")) {
          return key;
        }
      } else {
        // Standard check
        if (lowerUrl.includes(key)) {
          return key;
        }
      }
    }

    return "match medic";
  }

  /**
   * Get website source information from review data
   * @param {Object} reviewData - Review document from Firestore
   * @returns {Object} - Object with displayName, siteLogo, external_url, isExternal
   */
  getWebsiteSourceInfo(reviewData) {
    // Determine if external vs internal
    const isExternal = reviewData.is_external !== false;

    // Decide URL
    const external_url = isExternal
      ? reviewData.external_url || '#'
      : `/review/${reviewData.id || 'unknown'}`;

    // Find the website key
    let key = "match medic";
    if (isExternal && reviewData.external_url) {
      key = this.getWebsiteKeyFromUrl(reviewData.external_url);
    }

    // Pick display name & logo
    const displayName = this.displayNameMap[key] || this.displayNameMap["match medic"];
    const siteLogo = this.logoMap[key] || this.logoMap["match medic"];

    return {
      displayName,
      siteLogo,
      external_url,
      isExternal,
      websiteKey: key
    };
  }

  /**
   * Get logo for a specific website key
   * @param {string} websiteKey - The website key
   * @returns {string} - Logo import path
   */
  getLogo(websiteKey) {
    return this.logoMap[websiteKey] || this.logoMap["match medic"];
  }

  /**
   * Get display name for a specific website key
   * @param {string} websiteKey - The website key
   * @returns {string} - User-friendly display name
   */
  getDisplayName(websiteKey) {
    return this.displayNameMap[websiteKey] || this.displayNameMap["match medic"];
  }

  /**
   * Get all website keys in order of precedence
   * @returns {Array} - Array of website keys
   */
  getWebsiteKeys() {
    return [...this.websiteKeys];
  }

  /**
   * Get complete logo map
   * @returns {Object} - Logo map object
   */
  getLogoMap() {
    return { ...this.logoMap };
  }

  /**
   * Get complete display name map
   * @returns {Object} - Display name map object
   */
  getDisplayNameMap() {
    return { ...this.displayNameMap };
  }

  /**
   * Get website priority for sorting (used in ReviewGrid)
   * @param {Object} item - Item with type and siteForPriority properties
   * @returns {number} - Priority number (lower = higher priority)
   */
  getWebsitePriority(item) {
    if (item.type === "reddit") return 0;
    
    const site = (item.siteForPriority || "").toLowerCase();
    if (site.includes("reddit") || site.includes("youtube") || site.includes("tiktok")) {
      return 1;
    }
    
    return 2;
  }

  /**
   * Get website key from external_website field (legacy compatibility)
   * @param {string} externalWebsite - The external_website field value
   * @returns {string} - Website key
   */
  getWebsiteKeyFromExternalWebsite(externalWebsite) {
    if (!externalWebsite || typeof externalWebsite !== 'string') {
      return "match medic";
    }

    const lowerWebsite = externalWebsite.toLowerCase();
    
    // Check each website key
    for (const key of this.websiteKeys) {
      if (key === "hairrestorationnetwork") {
        if (lowerWebsite.includes("hair restoration network") || lowerWebsite.includes("hairrestorationnetwork")) {
          return key;
        }
      } else if (key === "hairlossexperiences") {
        if (lowerWebsite.includes("hair loss experiences") || lowerWebsite.includes("hairlossexperiences")) {
          return key;
        }
      } else if (lowerWebsite.includes(key)) {
        return key;
      }
    }

    return "match medic";
  }
}

// Create and export a singleton instance
const websiteUtils = new WebsiteUtils();
export default websiteUtils;